import {useMutation, useQuery, useQueryClient, UseQueryResult} from "@tanstack/react-query";
import {ApiClient} from "@/api/ApiClient";
import {TransLimitDto} from "@/common/dto/TransLimitDto";
import {CreateTransLimitReq} from "@/common/req/CreateTransLimitReq";
import {UpdateTransLimitReq} from "@/common/req/UpdateTransLimitReq";
import {TransLimitSummary} from "@/common/dto/TransLimitSummary";

export function useTransLimits(accountId: number, applyDateFrom: string | undefined, applyDateTo: string | undefined): UseQueryResult<TransLimitDto[]> {
    return useQuery({
        queryKey: ['transLimits', 'account', accountId, 'items', applyDateFrom + "-" + applyDateTo],
        queryFn: () => ApiClient.instance.getTransLimits(accountId, applyDateFrom, applyDateTo),
    });
}

export function useTransLimitsSummary(accountId: number | undefined, applyDateFrom: string | undefined, applyDateTo: string | undefined): UseQueryResult<TransLimitSummary[]> {
    return useQuery({
        queryKey: ['transLimits', 'account', accountId, 'summary', applyDateFrom + "-" + applyDateTo],
        queryFn: async () => ApiClient.instance.getTransLimitSummary(accountId, applyDateFrom, applyDateTo),
    });
}

export function useTransLimit(transLimitId: number): UseQueryResult<TransLimitDto> {
    return useQuery({
        queryKey: ['transLimit', transLimitId],
        queryFn: () => ApiClient.instance.getTransLimit(transLimitId),
    });
}

export function useCreateTransLimit() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (req: CreateTransLimitReq) => ApiClient.instance.createTransLimit(req),
        onSuccess: (item) => {
            void queryClient.invalidateQueries({queryKey: ['transLimits', 'account', item.accountId]});
        },
    });
}

export function useUpdateTransLimit() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({transLimitId, req}: { transLimitId: number, req: UpdateTransLimitReq }) =>
            ApiClient.instance.updateTransLimit(transLimitId, req),
        onSuccess: (data) => {
            void queryClient.invalidateQueries({queryKey: ['transLimits', 'account', data.accountId]});
            void queryClient.invalidateQueries({queryKey: ['transLimit', data.id]});
        },
    });
}

export function useDeleteTransLimit() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (transLimitId: number) => ApiClient.instance.deleteTransLimit(transLimitId),
        onSuccess: (item) => {
            void queryClient.invalidateQueries({queryKey: ['transLimits', 'account', item.accountId]});
        },
    });
}